/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * MobileBottomNav — main orchestrator.
 *
 * Fixed bottom bar visible only at <=768px. Shows main section icons,
 * morphs on tap to reveal sub-items via the user's chosen display style.
 *
 * - Collapsed: up to 5 section icons in a row (+ "More" if overflow)
 * - Expanded: tapped section anchors left, sub-items appear per display style
 * - 1-item sections: tap navigates directly without expanding
 * - Module switcher: configurable via tab / header / fab style
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, LayoutGrid } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useBottomNavStyle, useMobileModuleSwitchStyle } from '@/stores/layoutStore';
import { useModuleNavigation } from '@/hooks/useModuleNavigation';
import { BottomNavSectionTab } from './BottomNavSection';
import { HorizontalSubItems } from './HorizontalSubItems';
import { VerticalSubItems } from './VerticalSubItems';
import { GridSubItems } from './GridSubItems';
import { MoreOverlay } from './MoreOverlay';
import { ModuleSwitcherOverlay } from './ModuleSwitcherOverlay';
import { ModuleFab } from './ModuleFab';

const MAX_VISIBLE_TABS = 5;

export function MobileBottomNav() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const sections = useUIStore((s) => s.bottomNavSections);
  const {
    expandedSectionId,
    toggleBottomNavSection,
    collapseBottomNav,
    isMoreOverlayOpen,
    toggleMoreOverlay,
    isModuleSwitcherOpen,
    toggleModuleSwitcher,
  } = useUIStore();
  const bottomNavStyle = useBottomNavStyle();
  const moduleSwitchStyle = useMobileModuleSwitchStyle();
  const { availableModules, activeModule, showSwitcher } = useModuleNavigation();
  const barRef = useRef<HTMLDivElement>(null);

  // Collapse everything on route change
  useEffect(() => {
    collapseBottomNav();
  }, [location.pathname, collapseBottomNav]);

  // Detect active section based on current pathname
  const activeSectionId = sections.find((s) =>
    s.items.some((item) => location.pathname.startsWith(item.href))
  )?.id ?? null;

  const expandedSection = sections.find((s) => s.id === expandedSectionId);

  // Compute overflow: module tab takes one slot when style === 'tab'
  const showModuleTab = showSwitcher && moduleSwitchStyle === 'tab';
  const showModuleFab = showSwitcher && moduleSwitchStyle === 'fab';
  const maxSlots = showModuleTab ? MAX_VISIBLE_TABS - 1 : MAX_VISIBLE_TABS;

  const { visibleSections, overflowSections } = useMemo(() => {
    if (sections.length <= maxSlots) {
      return { visibleSections: sections, overflowSections: [] };
    }
    // Reserve last slot for "More"
    return {
      visibleSections: sections.slice(0, maxSlots - 1),
      overflowSections: sections.slice(maxSlots - 1),
    };
  }, [sections, maxSlots]);

  const hasOverflow = overflowSections.length > 0;

  // Badge dot on "More" tab if any overflow section has badges
  const overflowHasBadges = overflowSections.some((s) =>
    s.items.some((item) => (item.badge ?? 0) > 0)
  );

  // Active route is in overflow section
  const activeInOverflow = overflowSections.some((s) =>
    s.items.some((item) => location.pathname.startsWith(item.href))
  );

  const handleSectionTap = useCallback((sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    // 1-item sections navigate directly
    if (section.items.length === 1) {
      navigate(section.items[0].href);
      return;
    }

    toggleBottomNavSection(sectionId);
  }, [sections, navigate, toggleBottomNavSection]);

  const handleNavigate = useCallback(() => {
    collapseBottomNav();
  }, [collapseBottomNav]);

  // Handle "More" overlay section selection
  const handleMoreSectionSelect = useCallback((sectionId: string) => {
    // Close "More" overlay, then expand the selected section
    useUIStore.setState({ isMoreOverlayOpen: false });
    handleSectionTap(sectionId);
  }, [handleSectionTap]);

  // Handle module switch
  const handleModuleSelect = useCallback((mod: { basePath: string }) => {
    collapseBottomNav();
    navigate(mod.basePath);
  }, [navigate, collapseBottomNav]);

  // Close on outside click
  useEffect(() => {
    const anyOpen = !!expandedSectionId || isMoreOverlayOpen || isModuleSwitcherOpen;
    if (!anyOpen) return;

    const handleClick = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        collapseBottomNav();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expandedSectionId, isMoreOverlayOpen, isModuleSwitcherOpen, collapseBottomNav]);

  if (sections.length === 0) return null;

  const isExpanded = !!expandedSectionId;
  const anyOverlayOpen = isExpanded || isMoreOverlayOpen || isModuleSwitcherOpen;

  // Render sub-items based on user preference
  const renderSubItems = () => {
    if (!expandedSection) return null;

    const props = { items: expandedSection.items, onNavigate: handleNavigate };

    switch (bottomNavStyle) {
      case 'vertical':
        return <VerticalSubItems {...props} />;
      case 'grid':
        return <GridSubItems {...props} />;
      case 'horizontal':
      default:
        return <HorizontalSubItems {...props} />;
    }
  };

  return (
    <div className="bn-wrapper" ref={barRef}>
      {/* Grid/Vertical overlays render above the bar */}
      {isExpanded && (bottomNavStyle === 'grid' || bottomNavStyle === 'vertical') && (
        <div className="bn-overlay-container">
          {renderSubItems()}
        </div>
      )}

      {/* More overlay */}
      {isMoreOverlayOpen && (
        <div className="bn-overlay-container">
          <MoreOverlay
            sections={overflowSections}
            onSelectSection={handleMoreSectionSelect}
          />
        </div>
      )}

      {/* Module switcher overlay (for tab and fab styles) */}
      {isModuleSwitcherOpen && moduleSwitchStyle !== 'header' && (
        <div className="bn-overlay-container">
          <ModuleSwitcherOverlay
            modules={availableModules}
            activeModuleId={activeModule?.id}
            onSelect={handleModuleSelect}
          />
        </div>
      )}

      {/* FAB renders above the bar */}
      {showModuleFab && (
        <ModuleFab
          color={activeModule?.color ?? '#8B0000'}
          onClick={toggleModuleSwitcher}
          isOpen={isModuleSwitcherOpen}
        />
      )}

      <nav
        className="bn-bar"
        data-expanded={isExpanded}
        data-style={bottomNavStyle}
      >
        <div className="bn-tabs">
          {/* Module switcher tab (first slot) */}
          {showModuleTab && (
            <button
              className={`bn-section-tab bn-module-tab${isModuleSwitcherOpen ? ' bn-expanded' : ''}${isExpanded ? ' bn-faded' : ''}`}
              onClick={toggleModuleSwitcher}
              type="button"
              aria-label={t('bottomNav.modules', 'Modules')}
              style={{ '--mod-color': activeModule?.color ?? '#8B0000' } as React.CSSProperties}
            >
              <div className="bn-icon-wrap">
                <LayoutGrid size={24} style={{ color: activeModule?.color }} />
              </div>
              <span className="bn-tab-label" style={{ color: activeModule?.color }}>
                {activeModule?.shortName ?? t('bottomNav.modules', 'Modules')}
              </span>
            </button>
          )}

          {/* Visible section tabs */}
          {visibleSections.map((section) => (
            <BottomNavSectionTab
              key={section.id}
              section={section}
              isActive={activeSectionId === section.id}
              isExpanded={expandedSectionId === section.id}
              isFadedOut={anyOverlayOpen && expandedSectionId !== section.id && !isMoreOverlayOpen && !isModuleSwitcherOpen}
              onTap={() => handleSectionTap(section.id)}
            />
          ))}

          {/* "More" tab when overflow exists */}
          {hasOverflow && (
            <button
              className={`bn-section-tab${isMoreOverlayOpen ? ' bn-expanded' : ''}${activeInOverflow && !isMoreOverlayOpen ? ' bn-active' : ''}${isExpanded ? ' bn-faded' : ''}`}
              onClick={toggleMoreOverlay}
              type="button"
              aria-label={t('bottomNav.more', 'More')}
            >
              <div className="bn-icon-wrap">
                <MoreHorizontal size={24} />
                {(overflowHasBadges || activeInOverflow) && <span className="bn-badge-dot" />}
              </div>
              <span className="bn-tab-label">{t('bottomNav.more', 'More')}</span>
            </button>
          )}
        </div>

        {/* Horizontal sub-items render inside the bar */}
        {isExpanded && bottomNavStyle === 'horizontal' && renderSubItems()}
      </nav>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  /* Only show on mobile */
  .bn-wrapper {
    display: none;
  }

  @media (max-width: 768px) {
    .bn-wrapper {
      display: block;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 150;
    }

    .bn-bar {
      background: var(--color-bg-secondary);
      border-top: 1px solid var(--color-border-subtle);
      padding-bottom: env(safe-area-inset-bottom, 0px);
      display: flex;
      align-items: stretch;
      min-height: 64px;
    }

    .bn-tabs {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .bn-bar:not([data-expanded="true"]) .bn-tabs {
      flex: 1;
      justify-content: space-around;
    }

    .bn-bar[data-expanded="true"] .bn-tabs {
      width: auto;
    }

    /* Section tab */
    .bn-section-tab {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 8px 12px;
      min-width: 44px;
      min-height: 44px;
      background: transparent;
      border: none;
      cursor: pointer;
      color: var(--color-text-muted);
      transition: transform 250ms ease, opacity 250ms ease;
      will-change: transform, opacity;
      position: relative;
    }

    .bn-section-tab.bn-active {
      color: var(--color-brand, #8B0000);
    }

    .bn-section-tab.bn-expanded {
      color: var(--color-brand, #8B0000);
      border-bottom: 2px solid var(--color-brand, #8B0000);
    }

    .bn-section-tab.bn-faded {
      opacity: 0;
      transform: translateX(-20px);
      pointer-events: none;
      position: absolute;
    }

    .bn-icon-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .bn-badge-dot {
      position: absolute;
      top: -2px;
      right: -4px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #DC2626;
      border: 2px solid var(--color-bg-secondary);
    }

    .bn-tab-label {
      font-size: 10px;
      font-weight: 500;
      white-space: nowrap;
      line-height: 1;
    }

    /* ===== Module tab styling ===== */
    .bn-module-tab.bn-expanded {
      border-bottom-color: var(--mod-color, var(--color-brand, #8B0000));
    }

    /* ===== FAB ===== */
    .bn-fab {
      position: absolute;
      bottom: calc(100% + 12px);
      right: 16px;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: none;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
      transition: transform 200ms ease, box-shadow 200ms ease;
      z-index: 1;
    }

    .bn-fab:active {
      transform: scale(0.92);
    }

    .bn-fab-open {
      transform: rotate(45deg);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .bn-fab-open:active {
      transform: rotate(45deg) scale(0.92);
    }

    /* ===== More overlay panel ===== */
    .bn-more-panel {
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border-subtle);
      border-bottom: none;
      border-radius: 12px 12px 0 0;
      padding: 8px;
    }

    .bn-more-title {
      padding: 8px 12px 4px;
      font-size: 11px;
      font-weight: 600;
      color: var(--color-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .bn-more-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 8px;
      background: transparent;
      border: none;
      width: 100%;
      cursor: pointer;
      color: var(--color-text-secondary);
      font-size: 13px;
      font-weight: 500;
      text-align: left;
      transition: all 0.15s ease;
    }

    .bn-more-row:hover,
    .bn-more-active {
      background: rgba(139, 0, 0, 0.1);
      color: var(--color-brand, #8B0000);
    }

    .bn-more-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
    }

    .bn-more-label {
      flex: 1;
    }

    .bn-more-badge-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #DC2626;
      flex-shrink: 0;
    }

    /* ===== Module switcher overlay ===== */
    .bn-modsw-panel {
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border-subtle);
      border-bottom: none;
      border-radius: 12px 12px 0 0;
      padding: 12px;
    }

    .bn-modsw-title {
      padding: 4px 4px 8px;
      font-size: 11px;
      font-weight: 600;
      color: var(--color-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .bn-modsw-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }

    .bn-modsw-card {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid var(--color-border-subtle);
      background: var(--color-bg-primary);
      cursor: pointer;
      text-align: left;
      transition: all 0.15s ease;
      position: relative;
    }

    .bn-modsw-card:hover {
      border-color: var(--color-border-default);
      background: var(--color-bg-hover);
    }

    .bn-modsw-active {
      border-color: var(--mod-color, var(--color-brand));
      box-shadow: 0 0 0 1px var(--mod-color, var(--color-brand));
    }

    .bn-modsw-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .bn-modsw-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .bn-modsw-name {
      font-size: 12px;
      font-weight: 600;
      color: var(--color-text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .bn-modsw-sub {
      font-size: 10px;
      color: var(--color-text-muted);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .bn-modsw-check {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* ===== Horizontal sub-items (chips) ===== */
    .bn-horizontal-strip {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      overflow-x: auto;
      padding: 0 12px;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      opacity: 0;
      transform: translateX(30px);
      animation: bn-slide-in 250ms ease forwards;
    }

    .bn-horizontal-strip::-webkit-scrollbar {
      display: none;
    }

    .bn-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 20px;
      background: var(--color-bg-primary);
      border: 1px solid var(--color-border-subtle);
      color: var(--color-text-secondary);
      font-size: 12px;
      font-weight: 500;
      text-decoration: none;
      white-space: nowrap;
      scroll-snap-align: start;
      transition: all 0.15s ease;
      flex-shrink: 0;
    }

    .bn-chip:hover,
    .bn-chip-active {
      background: rgba(139, 0, 0, 0.1);
      color: var(--color-brand, #8B0000);
      border-color: var(--color-brand, #8B0000);
    }

    .bn-chip-badge {
      font-size: 10px;
      font-weight: 700;
      padding: 1px 5px;
      border-radius: 99px;
      background: var(--color-bg-tertiary);
      color: var(--color-text-muted);
    }

    .bn-chip-badge.danger {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
    }

    /* ===== Vertical slide-up panel ===== */
    .bn-overlay-container {
      opacity: 0;
      transform: translateY(10px);
      animation: bn-overlay-in 250ms ease forwards;
    }

    .bn-vertical-panel {
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border-subtle);
      border-bottom: none;
      border-radius: 12px 12px 0 0;
      padding: 8px;
      max-height: 50vh;
      overflow-y: auto;
    }

    .bn-vertical-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-radius: 8px;
      color: var(--color-text-secondary);
      font-size: 13px;
      font-weight: 500;
      text-decoration: none;
      transition: all 0.15s ease;
    }

    .bn-vertical-item:hover,
    .bn-vertical-active {
      background: rgba(139, 0, 0, 0.1);
      color: var(--color-brand, #8B0000);
    }

    .bn-vertical-badge {
      margin-left: auto;
      font-size: 10px;
      font-weight: 700;
      padding: 1px 5px;
      border-radius: 99px;
      background: var(--color-bg-tertiary);
      color: var(--color-text-muted);
    }

    .bn-vertical-badge.danger {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
    }

    /* ===== Grid overlay ===== */
    .bn-grid-overlay {
      background: var(--color-bg-secondary);
      border: 1px solid var(--color-border-subtle);
      border-bottom: none;
      border-radius: 12px 12px 0 0;
      padding: 12px;
      max-height: 50vh;
      overflow-y: auto;
    }

    .bn-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 4px;
    }

    .bn-grid-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 10px 4px;
      border-radius: 8px;
      color: var(--color-text-secondary);
      text-decoration: none;
      transition: all 0.15s ease;
    }

    .bn-grid-item:hover,
    .bn-grid-active {
      background: rgba(139, 0, 0, 0.1);
      color: var(--color-brand, #8B0000);
    }

    .bn-grid-icon-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .bn-grid-badge {
      position: absolute;
      top: -4px;
      right: -8px;
      font-size: 9px;
      font-weight: 700;
      padding: 0px 4px;
      border-radius: 99px;
      background: var(--color-bg-tertiary);
      color: var(--color-text-muted);
      min-width: 14px;
      text-align: center;
    }

    .bn-grid-badge.danger {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
    }

    .bn-grid-label {
      font-size: 10px;
      font-weight: 500;
      text-align: center;
      line-height: 1.2;
      max-width: 80px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* ===== Animations ===== */
    @keyframes bn-slide-in {
      from {
        opacity: 0;
        transform: translateX(30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes bn-overlay-in {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* ===== Reduced motion ===== */
    @media (prefers-reduced-motion: reduce) {
      .bn-section-tab,
      .bn-horizontal-strip,
      .bn-overlay-container,
      .bn-fab {
        transition: none !important;
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
      }
    }

    /* ===== Landscape ===== */
    @media (orientation: landscape) {
      .bn-bar {
        min-height: 52px;
      }

      .bn-fab {
        bottom: calc(100% + 8px);
      }
    }
  }
`;
